import * as React from "react";

import { keys, sum } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import QuestionnaireAccessTypeSelector from "./QuestionnaireAccessTypeSelector";
import { useUser } from "../utils/AccountStore";
import {
    Questionnaire,
    GetQuestionnaire,
    GetServiceManagerOrganization,
    GetServiceManagerOrganizations,
    ServiceManagerOrganization,
    CreateQuestionnaireAccessPolicy,
} from "../utils/ServiceManagerAPI";
import { GetGroups, SearchUsers } from "../utils/IdentityAPI";
import "../main.css";

const QuestionnaireAccessPoliciesCreate = () => {
    const navigate = useNavigate();
    const { questionnaireId } = useParams();
    const { user } = useUser();

    const [questionnaire, setQuestionnaire] = React.useState({} as Questionnaire);

    React.useEffect(() => {
        GetQuestionnaire(questionnaireId).then((res) => {
            setQuestionnaire(res.questionnaire);
        });
    }, [questionnaireId]);

    const [organizations, setOrganizations] = React.useState([] as ServiceManagerOrganization[]);

    React.useEffect(() => {
        GetServiceManagerOrganizations().then((organizations) => setOrganizations(organizations));
    }, []);

    const [accessLevels, setAccessLevels] = React.useState({});
    const [policyTarget, setPolicyTarget] = React.useState("");
    const [policyType, setPolicyType] = React.useState({ value: "membership", label: "Membership" });

    React.useEffect(() => {
        if (questionnaire.organizationID) {
            GetServiceManagerOrganization(questionnaire.organizationID).then((org) => {
                setTargetOrganization(org);
            });
        }
    }, [questionnaire]);

    const [targetOrganization, setTargetOrganization] = React.useState({} as ServiceManagerOrganization);

    const setQuestionnaireOrganizationMembership = (membershipType) => () => {
        setPolicyTarget(targetOrganization.identityID + ":" + membershipType);
    };

    const createAccessPolicy = () => {
        CreateQuestionnaireAccessPolicy({
            accessType: sum(keys(accessLevels).map((k) => +k)),
            questionnaireID: questionnaire.id,
            rule: policyTarget,
        }).then(() => {
            navigate(-1);
        });
    };

    const [groupSuggestions, setGroupSuggestions] = React.useState([] as any[]);
    const [userSuggestions, setUserSuggestions] = React.useState([] as any[]);

    React.useEffect(() => {
        if (policyType.value === "group") {
            GetGroups(questionnaire.organizationID).then((res) => setGroupSuggestions(res.identityGroups));
        } else if (policyType.value === "user") {
            SearchUsers(user, policyTarget).then((users) => setUserSuggestions(users));
        } else if (policyType.value == "public") {
            setPolicyTarget("*");
        }
    }, [policyType]);

    React.useEffect(() => {
        if (policyType.value === "user" && policyTarget.length > 3 && policyTarget.indexOf(":") === -1) {
            SearchUsers(user, policyTarget).then((users) => setUserSuggestions(users));
        }
    }, [policyTarget, policyType]);

    return (
        <div className="w-full max-w-4xl">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Access Policies</h3>
            {!user.isSingleOrganizationUser() && (
                <div className="flex flex-col gap-1 mb-4">
                    <label className="text-sm text-gray-700 dark:text-gray-300">Organization</label>
                    <select
                        value={targetOrganization.id || ""}
                        onChange={(e) => {
                            const selectedOrg = organizations.find(o => o.id == e.target.value);
                            setTargetOrganization(selectedOrg || {} as ServiceManagerOrganization);
                        }}
                        className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800"
                    >
                        <option value="">Select Organization...</option>
                        {organizations.map((org) => (
                            <option key={org.id} value={org.id}>
                                {org.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className="flex flex-col gap-1 mb-4">
                <label className="text-sm text-gray-700 dark:text-gray-300">Policy Type</label>
                <select
                    value={policyType.value}
                    onChange={(e) => {
                        const selected = [
                            { value: "membership", label: "Membership" },
                            { value: "group", label: "Group" },
                            { value: "user", label: "User" },
                            { value: "public", label: "Public" },
                        ].find(p => p.value == e.target.value);
                        setPolicyType(selected || { value: "membership", label: "Membership" });
                    }}
                    className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800"
                >
                    <option value="membership">Membership</option>
                    <option value="group">Group</option>
                    <option value="user">User</option>
                    <option value="public">Public</option>
                </select>
            </div>
            {policyType.value == "group" && (
                <div className="flex flex-col gap-1 mb-4">
                    <label className="text-sm text-gray-700 dark:text-gray-300">Group</label>
                    <input
                        type="text"
                        value={policyTarget}
                        onChange={(e) => setPolicyTarget(e.target.value)}
                        list="group-suggestions"
                        className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800"
                    />
                    <datalist id="group-suggestions">
                        {groupSuggestions
                            .filter((g) => new RegExp(policyTarget, "i").test(g.name))
                            .map((g) => (
                                <option key={g.id} value={g.id} label={`${g.name} ${g.groupCustomType ? "(" + g.groupCustomType.name + ")" : ""}`} />
                            ))}
                    </datalist>
                    {groupSuggestions
                        .filter((g) => new RegExp(policyTarget, "i").test(g.name))
                        .map((g) => (
                            <div 
                                key={g.id}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-2"
                                onClick={() => setPolicyTarget(g.id + ":Group")}
                            >
                                <span className="text-gray-900 dark:text-gray-100">
                                    {g.name} {g.groupCustomType && <span className="text-gray-500">({g.groupCustomType.name})</span>}
                                </span>
                            </div>
                        ))}
                </div>
            )}
            {policyType.value == "user" && (
                <div className="flex flex-col gap-1 mb-4">
                    <label className="text-sm text-gray-700 dark:text-gray-300">User</label>
                    <input
                        type="text"
                        value={policyTarget}
                        onChange={(e) => setPolicyTarget(e.target.value)}
                        list="user-suggestions"
                        className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800"
                    />
                    <datalist id="user-suggestions">
                        {userSuggestions.map((u) => (
                            <option key={u.id} value={u.id} label={u.fullName} />
                        ))}
                    </datalist>
                    {userSuggestions.map((u) => (
                        <div 
                            key={u.id}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                            onClick={() => setPolicyTarget(u.id + ":User")}
                        >
                            <span className="text-gray-900 dark:text-gray-100">{u.fullName}</span>
                        </div>
                    ))}
                </div>
            )}
            {policyType.value == "membership" && (
                <div className="flex flex-col gap-1 mb-4">
                    <label className="text-sm text-gray-700 dark:text-gray-300">Membership Type</label>
                    <div className="flex flex-row gap-2 p-2">
                        <button 
                            className={`px-4 py-2 rounded transition-colors ${policyTarget == targetOrganization.identityID + ":Staff" ? "bg-brand text-white" : "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"}`}
                            onClick={setQuestionnaireOrganizationMembership("Staff")}
                        >
                            All Staff
                        </button>
                        <button 
                            className={`px-4 py-2 rounded transition-colors ${policyTarget == targetOrganization.identityID + ":Student" ? "bg-brand text-white" : "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"}`}
                            onClick={setQuestionnaireOrganizationMembership("Student")}
                        >
                            All Students
                        </button>
                        <button 
                            className={`px-4 py-2 rounded transition-colors ${policyTarget == targetOrganization.identityID + ":Parent" ? "bg-brand text-white" : "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"}`}
                            onClick={setQuestionnaireOrganizationMembership("Parent")}
                        >
                            All Parents
                        </button>
                        <button 
                            className={`px-4 py-2 rounded transition-colors ${policyTarget == targetOrganization.identityID + ":Guest" ? "bg-brand text-white" : "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"}`}
                            onClick={setQuestionnaireOrganizationMembership("Guest")}
                        >
                            All Guests
                        </button>
                    </div>
                </div>
            )}
            <div className="flex flex-col gap-1 mb-4">
                <label className="text-sm text-gray-700 dark:text-gray-300">Questionnaire Access</label>
                <QuestionnaireAccessTypeSelector handleChange={(a) => setAccessLevels(a)} value={accessLevels}></QuestionnaireAccessTypeSelector>
            </div>
            <button 
                className="px-4 py-2 rounded bg-brand text-white hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={createAccessPolicy}
                disabled={!policyTarget || sum(keys(accessLevels).map((k) => +k)) == 0}
            >
                Create
            </button>
        </div>
    );
};
export default QuestionnaireAccessPoliciesCreate;
